/**
 * Tripnovac Visa — Dashboard Layout
 * Authenticated layout with sidebar + custom top header
 * Matches production design (sidebar nav, role badge, search, notifications)
 *
 * @company Alpha Libra LLC
 */

import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  InputBase,
  Chip,
  useMediaQuery,
  useTheme,
  Drawer,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  FlightTakeoff,
  Public as PublicIcon,
  ExpandMore,
  Menu as MenuIcon,
  Settings,
  AccountCircle,
  ExitToApp,
  ListAlt,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import Sidebar, { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from './Sidebar';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const { showSuccess } = useNotification();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElServices, setAnchorElServices] = useState<null | HTMLElement>(null);

  const effectiveSidebarWidth = isMobile ? 0 : (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH);

  const handleLogout = () => {
    logout();
    showSuccess('Logged out successfully');
    navigate('/');
    setAnchorElUser(null);
  };

  const getDashboardPath = () => {
    return '/dashboard';
  };

  const isActivePath = (path: string) => location.pathname === path;
  const isActivePrefix = (prefix: string) => location.pathname.startsWith(prefix);

  const getRoleBadge = () => {
    switch (user?.role) {
      case 'admin': return { label: 'Admin', color: '#f44336' };
      case 'agent': return { label: 'Agent', color: '#ff9800' };
      case 'corporate': return { label: 'Corporate', color: '#9c27b0' };
      case 'enterprise': return { label: 'Enterprise', color: '#2196f3' };
      default: return { label: 'Standard', color: '#29B848' };
    }
  };

  const badge = getRoleBadge();

  // Top nav tabs matching production screenshots
  const navTabs = [
    { label: 'Dashboard', path: getDashboardPath(), icon: <DashboardIcon sx={{ fontSize: 18 }} /> },
    { label: 'Visa Check', path: '/visa-check', icon: <SearchIcon sx={{ fontSize: 18 }} /> },
    { label: 'Applications', path: '/applications', icon: <ListAlt sx={{ fontSize: 18 }} /> },
    { label: 'Countries', path: '/countries', icon: <PublicIcon sx={{ fontSize: 18 }} /> },
  ];

  const userMenuItems = [
    { label: 'Profile', path: '/profile', icon: <AccountCircle /> },
    { label: 'Dashboard', path: getDashboardPath(), icon: <DashboardIcon /> },
    { label: 'Applications', path: '/applications', icon: <ListAlt /> },
    { label: 'Settings', path: '/settings', icon: <Settings /> },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#030B1B' }}>
      {/* Sidebar — Desktop */}
      {!isMobile && (
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      )}

      {/* Sidebar — Mobile Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, border: 'none' } }}
        >
          <Sidebar onToggle={() => setMobileDrawerOpen(false)} />
        </Drawer>
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          ml: `${effectiveSidebarWidth}px`,
          transition: 'margin-left 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Top Header Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'rgba(3, 11, 27, 0.85)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            color: '#FFFFFF',
          }}
        >
          <Toolbar sx={{ px: { xs: 2, sm: 3 }, gap: 1, minHeight: '64px !important' }}>
            {/* Mobile menu button */}
            {isMobile && (
              <IconButton
                edge="start"
                color="primary"
                onClick={() => setMobileDrawerOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Role Badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}
              >
                {user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
              <Chip
                label={badge.label}
                size="small"
                sx={{
                  bgcolor: badge.color,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24,
                  borderRadius: '12px',
                }}
              />
            </Box>

            {/* Navigation Tabs */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                {navTabs.map((tab) => {
                  const active = tab.path === '/' ? isActivePath('/') :
                    isActivePath(tab.path) || (tab.path !== '/' && isActivePrefix(tab.path));
                  return (
                    <Button
                      key={tab.path}
                      onClick={() => navigate(tab.path)}
                      startIcon={tab.icon}
                      sx={{
                        color: active ? '#0EA5E9' : 'rgba(255, 255, 255, 0.7)',
                        fontWeight: active ? 600 : 400,
                        fontSize: '0.85rem',
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 1.5,
                        borderBottom: active ? '2px solid' : '2px solid transparent',
                        borderColor: active ? '#0EA5E9' : 'transparent',
                        borderRadius: 0,
                        '&:hover': {
                          bgcolor: 'rgba(14, 165, 233, 0.08)',
                          color: '#0EA5E9',
                        },
                      }}
                    >
                      {tab.label}
                    </Button>
                  );
                })}

                {/* Services Dropdown */}
                <Button
                  onClick={(e) => setAnchorElServices(e.currentTarget)}
                  endIcon={<ExpandMore />}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 400,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 1.5,
                    borderBottom: '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': {
                      bgcolor: 'rgba(14, 165, 233, 0.08)',
                      color: '#0EA5E9',
                    },
                  }}
                >
                  Services
                </Button>
                <Menu
                  anchorEl={anchorElServices}
                  open={Boolean(anchorElServices)}
                  onClose={() => setAnchorElServices(null)}
                >
                  <MenuItem onClick={() => { navigate('/visa-check'); setAnchorElServices(null); }}>
                    <SearchIcon sx={{ mr: 1, fontSize: 18 }} /> Visa Checker
                  </MenuItem>
                  <MenuItem onClick={() => { navigate('/pricing'); setAnchorElServices(null); }}>
                    <FlightTakeoff sx={{ mr: 1, fontSize: 18 }} /> Pricing
                  </MenuItem>
                  <MenuItem onClick={() => { navigate('/help'); setAnchorElServices(null); }}>
                    <PublicIcon sx={{ mr: 1, fontSize: 18 }} /> Help Center
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {/* Right Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              {/* Search Bar */}
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.5,
                    minWidth: 200,
                  }}
                >
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: 20, mr: 1 }} />
                  <InputBase
                    placeholder="Search applications, countries..."
                    sx={{ fontSize: '0.85rem', flex: 1, color: 'white', '& ::placeholder': { color: 'rgba(255,255,255,0.4)' } }}
                  />
                </Box>
              )}

              {/* Notifications */}
              <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                <Badge badgeContent={3} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              {/* User Avatar */}
              <IconButton
                size="small"
                onClick={(e) => setAnchorElUser(e.currentTarget)}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: 'primary.main',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  {user?.firstName?.charAt(0)?.toUpperCase() || user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={() => setAnchorElUser(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {userMenuItems.map((item) => (
                  <MenuItem key={item.path} onClick={() => { navigate(item.path); setAnchorElUser(null); }}>
                    {React.cloneElement(item.icon as React.ReactElement, { sx: { mr: 1, fontSize: 18 } })} {item.label}
                  </MenuItem>
                ))}
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <ExitToApp sx={{ mr: 1, fontSize: 18 }} /> Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3 },
            bgcolor: '#030B1B',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
